import { useEffect, useMemo, useRef, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'

export interface ApiDataEditorProps {
  apiData: Record<string, unknown> | undefined
  onChange: (next: Record<string, unknown>) => void
  disabled?: boolean
}

/** Objeto JSON só com um nível e valores primitivos → editável em tabela. */
function isFlatPrimitiveRecord(obj: unknown): obj is Record<string, string | number | boolean | null> {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false
  return Object.values(obj as Record<string, unknown>).every(
    (v) =>
      v === null ||
      typeof v === 'string' ||
      typeof v === 'number' ||
      typeof v === 'boolean'
  )
}

function recordToStringRows(obj: Record<string, unknown>): [string, string][] {
  return Object.entries(obj).map(([k, v]) => {
    if (v === null) return [k, 'null']
    if (typeof v === 'boolean') return [k, v ? 'true' : 'false']
    return [k, String(v)]
  })
}

function rowsToRecord(rows: [string, string][]): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, raw] of rows) {
    const key = k.trim()
    if (!key) continue
    const t = raw.trim()
    if (t === 'true') {
      out[key] = true
      continue
    }
    if (t === 'false') {
      out[key] = false
      continue
    }
    if (t === 'null') {
      out[key] = null
      continue
    }
    if (/^-?\d+(\.\d+)?$/.test(t)) {
      const n = Number(t)
      if (!Number.isNaN(n)) {
        out[key] = n
        continue
      }
    }
    out[key] = raw
  }
  return out
}

/**
 * Corpo JSON plano (chave → valor simples) em tabela; JSON aninhado só no modo avançado.
 */
export function ApiDataEditor({ apiData, onChange, disabled = false }: ApiDataEditorProps) {
  const raw = (apiData && typeof apiData === 'object' && !Array.isArray(apiData)
    ? apiData
    : {}) as Record<string, unknown>

  const canUseSimple = isFlatPrimitiveRecord(raw)
  const [advanced, setAdvanced] = useState(!canUseSimple)

  const externalRows = useMemo(() => recordToStringRows(raw), [raw])
  const rowIdRef = useRef(0)
  const [rows, setRowsState] = useState<Array<{ id: number; key: string; val: string }>>(
    externalRows.map(([k, v]) => ({ id: ++rowIdRef.current, key: k, val: v }))
  )

  const jsonText = useMemo(() => JSON.stringify(raw, null, 2), [raw])
  const [jsonDraft, setJsonDraft] = useState(jsonText)
  useEffect(() => {
    setJsonDraft(jsonText)
  }, [jsonText, advanced])
  useEffect(() => {
    const projected = rowsToRecord(rows.map((r) => [r.key, r.val]))
    if (JSON.stringify(projected) === JSON.stringify(raw)) return
    setRowsState(externalRows.map(([k, v]) => ({ id: ++rowIdRef.current, key: k, val: v })))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalRows])

  const commitRows = (next: Array<{ id: number; key: string; val: string }>) => {
    setRowsState(next)
    onChange(rowsToRecord(next.map((r) => [r.key, r.val])))
  }

  const updateRow = (rowId: number, patch: { key?: string; val?: string }) => {
    const copy = rows.map((r) =>
      r.id === rowId
        ? { ...r, key: patch.key !== undefined ? patch.key : r.key, val: patch.val !== undefined ? patch.val : r.val }
        : r
    )
    commitRows(copy)
  }

  const addRow = () => {
    setRowsState((prev) => [...prev, { id: ++rowIdRef.current, key: '', val: '' }])
  }
  const removeRow = (rowId: number) => commitRows(rows.filter((r) => r.id !== rowId))

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={disabled || !canUseSimple}
          onClick={() => setAdvanced(false)}
          className={`text-xs px-2 py-1 rounded border ${
            !advanced ? 'bg-blue-50 border-blue-300 text-blue-800' : 'border-gray-200 text-gray-600'
          }`}
        >
          Chave / valor
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setAdvanced(true)}
          className={`text-xs px-2 py-1 rounded border ${
            advanced ? 'bg-blue-50 border-blue-300 text-blue-800' : 'border-gray-200 text-gray-600'
          }`}
        >
          JSON avançado
        </button>
        {!canUseSimple && (
          <span className="text-xs text-amber-700">Estrutura aninhada: use JSON avançado.</span>
        )}
      </div>

      {advanced ? (
        <textarea
          value={jsonDraft}
          disabled={disabled}
          onChange={(e) => {
            const t = e.target.value
            setJsonDraft(t)
            try {
              const parsed = JSON.parse(t || '{}') as unknown
              if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                onChange(parsed as Record<string, unknown>)
              } else {
                onChange({})
              }
            } catch {
              /* texto inválido: só atualiza rascunho até JSON válido */
            }
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
          rows={6}
          placeholder='{"cpf": "{{cpf}}"}'
        />
      ) : (
        <div className="rounded-md border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="text-left px-2 py-1.5 font-medium">Chave</th>
                <th className="text-left px-2 py-1.5 font-medium">Valor</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-2 py-3 text-gray-500 text-center">
                    Nenhum campo. Adicione linhas ou use JSON avançado.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="border-t border-gray-100">
                    <td className="p-1">
                      <input
                        type="text"
                        disabled={disabled}
                        value={row.key}
                        onChange={(e) => updateRow(row.id, { key: e.target.value })}
                        className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                        placeholder="cpf"
                      />
                    </td>
                    <td className="p-1">
                      <input
                        type="text"
                        disabled={disabled}
                        value={row.val}
                        onChange={(e) => updateRow(row.id, { val: e.target.value })}
                        className="w-full px-2 py-1 border border-gray-200 rounded text-sm font-mono"
                        placeholder="{{cpf}} ou true / false / número"
                      />
                    </td>
                    <td className="p-1 text-center">
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => removeRow(row.id)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        aria-label="Remover"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div className="p-2 border-t border-gray-100">
            <button
              type="button"
              disabled={disabled}
              onClick={addRow}
              className="inline-flex items-center gap-1.5 px-2 py-1.5 text-sm text-blue-700 border border-blue-200 rounded-md hover:bg-blue-50"
            >
              <Plus className="w-4 h-4" />
              Adicionar campo
            </button>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-500">
        Em chave/valor: números e true/false/null são reconhecidos; o resto fica texto (útil para{' '}
        <code className="bg-gray-100 px-0.5 rounded">{'{{variáveis}}'}</code>).
      </p>
    </div>
  )
}
